/* eslint-disable react/react-in-jsx-scope -- Unaware of jsxImportSource */
/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Layout from './components/layout/layout/layout';
import Lead from './components/Lead/Lead';
import Login from './components/Login/Login';
import VideoBackground from './components/VideoBackground/VideoBackground';

// Image imports
import gui from './gui-dashboard@2x.png';
import monitor from './monitor.png';
import screening from './screening@2x.png';
import inspection from './domain-inspection@2x.png';
import realtime from './real-time@2x.png';
import domainwatch from './domainwatch.png';

const App = () => {
  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          paddingTop: { xs: 6, sm: 0 },
          paddingBottom: { xs: 6, sm: 0 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          height: { sm: 'calc(100vh - 80px)' },
        }}
      >
        <Container
          component="main"
          maxWidth="xl"
          sx={{
            position: 'relative',
            zIndex: 30,
          }}
        >
          <Grid container>
            <Grid item lg={1} />
            <Grid item xs={12} sm={6} md={7} lg={6}>
              <Lead />
            </Grid>
            <Grid item xs={12} sm={6} md={5} lg={4}>
              <Login />
            </Grid>
          </Grid>
        </Container>
        <VideoBackground />
      </Box>
      <Container
        component="main"
        maxWidth="xl"
        css={css`
          padding-top: 3rem;

          @media (min-width: 900px) {
            padding-top: 8rem;
          }
        `}
      >
        <div
          className="grid gap-4 grid-cols-12"
          css={css`
            margin-bottom: 4rem;

            @media (min-width: 768px) {
              margin-bottom: 8rem;
            }
          `}
        >
          <div
            css={css`
              grid-column: 1 / -1;
              margin-bottom: 5.5rem;

              @media (min-width: 768px) {
                grid-column: 2 / span 5;
                margin-bottom: 0;
                padding-right: 2rem;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
                padding-top: 75%;
                position: relative;
              `}
            >
              <div
                css={css`
                  position: absolute;
                  inset: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <img
                  src={gui}
                  alt="Probe's UI"
                  width="606"
                  height="391"
                  css={css`
                    display: block;
                    max-width: 100%;
                    height: auto;
                    margin: 0 auto 1.5rem;
                    box-shadow: 10px 40px 80px rgba(0, 0, 0, 0.08);

                    @media (min-width: 600px) {
                      margin-bottom: 3rem;
                    }
                  `}
                />
              </div>
            </div>
            <div>
              <Typography
                variant="h3"
                lineHeight={1}
                sx={{
                  marginBottom: { xs: '0.75rem', md: '1.25rem' },
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                }}
              >
                Flexible platform
              </Typography>

              <Typography variant="body2">
                The majority of our partners use our web based service to set up
                recurring domain name scans on behalf of their clients. To keep
                track of domain name registrations containing or confusingly
                similar to ones own brand / trademark is a hygiene factor in the
                modern digital world. Our flexible platform and bulk features
                also makes it possible to get an overview over domain name
                portfolios.
              </Typography>
            </div>
          </div>
          <div
            css={css`
              grid-column: 1 / -1;

              @media (min-width: 768px) {
                grid-column: 7 / span 5;
                display: flex;
                flex-direction: column;
                padding-left: 2rem;
              }
            `}
          >
            <div
              css={css`
                width: 100%;
                padding-top: 75%;
                position: relative;
              `}
            >
              <div
                css={css`
                  position: absolute;
                  inset: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="245"
                  height="266"
                  viewBox="0 0 245 266"
                  css={css`
                    display: block;
                    margin: auto;

                    @media (max-width: 767px) {
                      max-width: 12rem;
                    }
                  `}
                >
                  <path
                    fill="#1621DB"
                    fillRule="evenodd"
                    d="M125.223 179.991a2.553 2.553 0 0 1 1.261 2.253v47.958a17.725 17.725 0 0 1 10.023 5.027 17.78 17.78 0 1 1-15.127-5.025l-.001-47.96a2.553 2.553 0 0 1 3.844-2.253Zm-13.001 62.962a12.675 12.675 0 0 0 20.673 13.812 12.635 12.635 0 0 0 3.707-8.603l.005-.36a12.675 12.675 0 0 0-24.385-4.849Zm39.851-100.32v25.17c.007.075.01.151.01.227v8.477l29.134 21.669a2.55 2.55 0 0 1 1.2 2.163v16.085a17.725 17.725 0 0 1 10.018 5.026 17.78 17.78 0 1 1-15.122-5.026l-.001-14.81-29.134-21.671a2.551 2.551 0 0 1-1.2-2.163v-9.763a2.453 2.453 0 0 1-.01-.228v-25.156c0-3.219 5.105-3.333 5.105 0Zm-53.488 28.271a2.553 2.553 0 0 1 1.261 2.253v4.435a2.636 2.636 0 0 1-1.5 2.513L67.338 201.63v14.803a17.725 17.725 0 0 1 9.636 4.708l.316.309a17.78 17.78 0 1 1-15.056-5.035l-.001-15.615a2.552 2.552 0 0 1 .513-2.044l-.005-.003c.232-.308.53-.56.874-.737l31.126-21.607v-3.252a2.553 2.553 0 0 1 3.844-2.253Zm69.565 58.27a12.675 12.675 0 0 0 20.673 13.812 12.636 12.636 0 0 0 3.712-8.963 12.675 12.675 0 0 0-24.385-4.849Zm-103.815-7.818-.25.007a12.675 12.675 0 1 0 9.594 21.623 12.635 12.635 0 0 0 3.706-8.603l.005-.36a12.675 12.675 0 0 0-12.18-12.666c-.29.052-.586.05-.875-.001Zm120.249-62.573a2.553 2.553 0 0 1 1.261 2.253v8.475l30.8 22.912a17.737 17.737 0 0 1 10.232-3.24 17.725 17.725 0 0 1 12.573 5.208 17.78 17.78 0 1 1-26.555 1.587l-31.118-23.149a2.545 2.545 0 0 1-1.054-2.347l.017-.119v-9.327a2.553 2.553 0 0 1 3.844-2.253Zm30.58 43.332a12.675 12.675 0 0 0 20.674 13.812 12.636 12.636 0 0 0 3.712-8.963 12.675 12.675 0 0 0-24.385-4.849ZM70.962 142.632l.001 21.781c0 .912-.486 1.755-1.276 2.211L33.71 191.487a17.782 17.782 0 0 1-22.725 24.339 17.779 17.779 0 0 1 6.796-34.206 17.725 17.725 0 0 1 12.573 5.208l.002-.002c.156.156.31.315.459.476l35.041-24.218v-20.452c0-2.986 5.105-3.194 5.105 0ZM6.071 194.55a12.675 12.675 0 0 0 20.673 13.812 12.636 12.636 0 0 0 3.707-8.603l.005-.36a12.675 12.675 0 0 0-24.385-4.849Zm117.861-29.808a2.553 2.553 0 1 1 0 5.106 2.553 2.553 0 0 1 0-5.106ZM98.59 150.318a2.553 2.553 0 0 1 1.261 2.253v7.756a2.553 2.553 0 0 1-5.105 0v-7.756a2.553 2.553 0 0 1 3.844-2.253Zm84.702-7.1a2.553 2.553 0 1 1 0 5.106 2.553 2.553 0 0 1 0-5.106Zm-59.361-.31a2.553 2.553 0 1 1 0 5.106 2.553 2.553 0 0 1 0-5.106ZM144.247-.003a2.623 2.623 0 0 1 2.623 2.627v17.837a92.928 92.928 0 0 1 29.165 12.084L188.65 19.93a2.623 2.623 0 0 1 3.71 0l25.655 25.655a2.623 2.623 0 0 1 0 3.71L205.401 61.91a92.922 92.922 0 0 1 12.083 29.165h17.838a2.624 2.624 0 0 1 2.624 2.624v36.282a2.624 2.624 0 0 1-2.624 2.624H215.37a2.624 2.624 0 0 1 0-5.247h17.327V96.322H215.37a2.624 2.624 0 0 1-2.611-2.362 88.131 88.131 0 0 0-12.757-30.763 2.624 2.624 0 0 1 .188-3.5l12.26-12.26-21.944-21.94-12.2 12.2a2.629 2.629 0 0 1-3.557.244 88.139 88.139 0 0 0-30.76-12.758 2.624 2.624 0 0 1-2.362-2.611V5.247h-31.039v17.327a2.624 2.624 0 0 1-2.362 2.611A88.133 88.133 0 0 0 77.47 37.943a2.624 2.624 0 0 1-3.5-.188L61.706 25.497 39.761 47.44l12.26 12.26a2.624 2.624 0 0 1 .188 3.5 88.135 88.135 0 0 0-12.758 30.76 2.624 2.624 0 0 1-2.611 2.362H19.513v31.035H36.84a2.624 2.624 0 0 1 0 5.248H16.89a2.625 2.625 0 0 1-2.624-2.625V93.697a2.624 2.624 0 0 1 2.624-2.624h17.837a92.919 92.919 0 0 1 12.084-29.165L34.196 49.297a2.623 2.623 0 0 1 0-3.71l25.655-25.655a2.623 2.623 0 0 1 3.71 0l12.615 12.615a92.911 92.911 0 0 1 29.165-12.084V2.624a2.624 2.624 0 0 1 2.624-2.627h36.282ZM88.41 64.352a4.21 4.21 0 0 1 2.53.867 5.747 5.747 0 0 1 1.816 2.515l.092.272 19.657 53.737c.26.722.231 1.517-.081 2.219a2.2 2.2 0 0 1-1.352 1.189 2.554 2.554 0 0 1-1.894-.081 3.391 3.391 0 0 1-1.347-1.53l-.087-.215-6.656-18.467H75.64l-6.737 18.467a3.208 3.208 0 0 1-1.5 1.745 2.164 2.164 0 0 1-1.827.162 2.389 2.389 0 0 1-1.42-1.272 2.518 2.518 0 0 1-.016-2.044l.084-.175 19.589-53.736a5.701 5.701 0 0 1 1.891-2.786 4.461 4.461 0 0 1 2.706-.867Zm43.724 1.21.282.009 15.778-.001c5.116-.004 9.183 1.692 12.2 5.087a16.389 16.389 0 0 1 4.113 11.351 16.2 16.2 0 0 1-4.113 11.2c-2.924 3.335-6.838 5.051-11.741 5.15l-.449.004h-18.361v24.586a2.441 2.441 0 0 1-.8 1.728 2.471 2.471 0 0 1-1.472.63l-.206.006a2.333 2.333 0 0 1-2.486-2.358l.01-.185v-49.5a7.907 7.907 0 0 1 1.827-5.317 7.142 7.142 0 0 1 5.418-2.39Zm50.512-1.292a2.476 2.476 0 0 1 1.678.637c.5.466.787 1.112.802 1.79l-.002.185v56.065a2.441 2.441 0 0 1-.8 1.728 2.471 2.471 0 0 1-1.472.63l-.206.006a2.333 2.333 0 0 1-2.486-2.358l.01-.185V66.882a2.68 2.68 0 0 1 .717-1.975 2.606 2.606 0 0 1 1.759-.637ZM88.48 70.345h-.23l-11.026 30.129h22.2L88.48 70.345Zm59.41-.32-.349.01h-14.219a3.338 3.338 0 0 0-2.692 1.109 3.709 3.709 0 0 0-.789 2.24l.004.222v20.375h17.7a11.364 11.364 0 0 0 8.925-3.721 11.959 11.959 0 0 0 3-8.171 12.178 12.178 0 0 0-3-8.334 11.363 11.363 0 0 0-8.58-3.73Z"
                  />
                </svg>
              </div>
            </div>
            <Box>
              <Typography
                variant="h3"
                lineHeight={1}
                sx={{
                  marginBottom: { xs: '0.75rem', md: '1.25rem' },
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                }}
              >
                API
              </Typography>
              <Typography variant="body2">
                Our real-time scan technology powers many of the worlds largest
                online brand protection platforms, serving on-demand scans via
                API for thousands of brands. A service is never better than the
                data it can provide and by adding our detection engine to the
                data gathering backends or SIEM-systems increases data quality
                and threat detection capabilities.
              </Typography>
            </Box>
          </div>
        </div>

        <div
          className="grid gap-4 grid-cols-12"
          css={css`
            position: relative;
            margin: 0 -1rem 4rem;
            padding: 4rem 1rem;
            background-color: #1621db;
            color: #fff;

            @media (min-width: 600px) {
              padding: 3rem 1.5rem;
              margin: 0 0 6rem 0;
            }

            @media (min-width: 768px) {
              padding: 6rem 0;
            }

            @media (min-width: 1024px) {
              padding: 8rem 0;
            }
          `}
        >
          <div
            css={css`
              grid-column: 1 / -1;
              margin-bottom: 3rem;

              @media (min-width: 768px) {
                grid-column: 2 / span 5;
                margin-bottom: 0;
              }
            `}
          >
            <Typography
              variant="body2"
              sx={{
                marginBottom: '.25rem',
                fontSize: { xs: '1.125rem', md: '1.5rem' },
              }}
            >
              FBI Internet Crime Report 2020
            </Typography>

            <Typography
              variant="body2"
              lineHeight={1.1}
              sx={{
                marginBottom: '1.5rem',
                fontSize: { xs: '2rem', md: '2.5rem', lg: '3.125rem' },
                fontWeight: 700,
              }}
            >
              <span
                css={css`
                  display: block;
                `}
              >
                Loss
              </span>{' '}
              <span
                css={css`
                  display: block;
                  color: #9fe3fc;
                `}
              >
                $1,866,642,107
              </span>
            </Typography>

            <Typography
              variant="body2"
              textTransform="uppercase"
              fontWeight={600}
            >
              Crime Types:
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '1rem', md: '1.5rem' },
                fontWeight: 700,
              }}
            >
              Business email compromise (BEC) <br />
              and email account compromise (EAC)
            </Typography>
          </div>
          <div
            css={css`
              grid-column: 1 / -1;

              @media (min-width: 768px) {
                grid-column: 7 / span 5;
              }
            `}
          >
            <Typography
              variant="h3"
              gutterBottom
              lineHeight={1.1}
              sx={{
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                fontWeight: 700,
              }}
            >
              <span
                css={css`
                  color: #9fe3fc;
                `}
              >
                Detecting threats
              </span>{' '}
              in the domain name system
            </Typography>
            <Typography variant="body2" gutterBottom>
              The landscape of Internet domain name abuse evolves rapidly. As
              the world increase their Internet presence, so does the cyber
              criminals. Brand owners are exposed to risks from brand dilution,
              reputation erosion, counterfeit, fraud and other scams. Phishing
              is the most frequent attack vector and reported Business Email
              Compromise (BEC) losses in the US exceeded $1.8 Billion in 2020
              (FBI 2020 Internet Crime Report).
            </Typography>
            <Link
              href="#"
              sx={{
                color: '#9fe3fc',
                fontSize: { xs: '1.25rem', md: '1.75rem' },
                fontWeight: 700,
                textDecorationColor: '#9fe3fc',
                WebkitTextDecorationColor: '#9fe3fc',
                textDecorationThickness: '3px',
                textUnderlineOffset: '8px',
                '&:hover': {
                  color: '#fff',
                  textDecorationColor: '#fff',
                  WebkitTextDecorationColor: '#fff',
                },
              }}
            >
              Read the report
            </Link>
          </div>
        </div>

        <Grid
          container
          justifyContent="center"
          alignItems="center"
          colSpacing={0}
          rowSpacing={8}
          sx={{
            flexDirection: { xs: 'column', md: 'column-reverse' },
            marginBottom: { xs: '6rem', md: '8rem' },
          }}
        >
          <Grid item xs={12} sx={{ width: '100%' }}>
            <Box
              css={css`
                padding-top: 4rem;
                padding-left: 1.5rem;
                padding-right: 1.5rem;
                background-color: #dee5f4;

                @media (min-width: 600px) {
                  padding-top: 6rem;
                }
              `}
            >
              <img
                src={domainwatch}
                alt=""
                width="1364"
                height="703"
                css={css`
                  margin-left: auto;
                  margin-right: auto;
                  display: block;
                  max-width: 100%;
                  height: auto;
                `}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={10} md={8} lg={6} xl={5}>
            <Typography
              variant="h3"
              lineHeight={1}
              sx={{
                marginBottom: { xs: '0.75rem', md: '1.25rem' },
                fontSize: { xs: '1.75rem', md: '2.5rem' },
              }}
            >
              A strong and scalable solution
            </Typography>
            <Typography variant="body2" gutterBottom>
              The Probe.net domain name scan and monitor system is built for
              portfolio managers and brand protection companies to assist in
              tracking and monitor their clients brands among almost 370 million
              registered domain names (Verisign DNIB Q3 2021) in more than 1750
              different top and second level domains. The system provides data
              and services by Graphical User Interface or API.
            </Typography>
            <Link
              href="#"
              sx={{
                fontSize: '1.75rem',
                fontWeight: 700,
                textDecorationColor: '#0c45e6',
                WebkitTextDecorationColor: '#0c45e6',
                textDecorationThickness: '3px',
                textUnderlineOffset: '8px',
              }}
            >
              Contact us
            </Link>
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={2}
          rowSpacing={4}
          sx={{ marginBottom: { xs: 8, sm: 0 } }}
        >
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box
                className="aspect-ratio-container-inner"
                sx={{
                  backgroundColor: '#dee5f4',
                  padding: { xs: '1.5rem', md: '3rem', lg: '5rem', xl: '6rem' },
                }}
              >
                <img
                  src={realtime}
                  alt=""
                  width="608"
                  height="497"
                  css={css`
                    display: block;
                    max-width: 100%;
                    height: auto;
                    box-shadow: 10px 40px 80px rgba(0, 0, 0, 0.08);
                  `}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box className="aspect-ratio-container-inner">
                <Typography
                  variant="h3"
                  lineHeight={1}
                  sx={{
                    marginBottom: { xs: '0.75rem', md: '1.25rem' },
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                  }}
                >
                  Real-time scan technology
                </Typography>
                <Typography variant="body2">
                  Gathering domain name intelligence is a resource intensive
                  process. The domain name landscape is rapidly transforming and
                  data accuracy is an important parameter for related ventures.
                  Probe.net utilises proprietary scan technology to focus
                  resources during scan missions, taking snapshots of the
                  Internet in real-time.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={2}
          rowSpacing={4}
          direction="row-reverse"
          sx={{ marginBottom: { xs: 8, sm: 0 } }}
        >
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box
                className="aspect-ratio-container-inner"
                sx={{
                  paddingTop: { xs: '1.5rem', lg: 0 },
                  paddingRight: { xs: '1.5rem', lg: 0 },
                  paddingBottom: { xs: '1.5rem', lg: 0 },
                  paddingLeft: { xs: 0, lg: 0 },
                  backgroundColor: '#dee5f4',
                }}
              >
                <img
                  src={inspection}
                  alt=""
                  width="611"
                  height="703"
                  css={css`
                    display: block;
                    max-width: 100%;
                    height: auto;
                    box-shadow: 10px 40px 80px rgba(0, 0, 0, 0.08);
                  `}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box className="aspect-ratio-container-inner bg-grey">
                <Typography
                  variant="h3"
                  lineHeight={1}
                  sx={{
                    marginBottom: { xs: '0.75rem', md: '1.25rem' },
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                  }}
                >
                  Domain name inspection
                </Typography>
                <Typography variant="body2">
                  Gain access to in-depth data regarding specific domain names.
                  We gather intelligence from various sources to help you in the
                  screening of potential infringements or to aid you in
                  migration, portfolio merger or transfer cases.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={2}
          rowSpacing={4}
          sx={{ marginBottom: { xs: 8, sm: 0 } }}
        >
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box
                className="aspect-ratio-container-inner"
                sx={{
                  padding: { xs: '1.5rem', md: '3rem', lg: '5rem', xl: '6rem' },
                  backgroundColor: '#dee5f4',
                }}
              >
                <img
                  src={monitor}
                  alt=""
                  width="608"
                  height="521"
                  css={css`
                    display: block;
                    max-width: 100%;
                    height: auto;
                    margin: 0 auto;
                    box-shadow: 10px 40px 80px rgba(0, 0, 0, 0.08);
                    border-radius: 0.3125rem;
                  `}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box className="aspect-ratio-container-inner">
                <Typography
                  variant="h3"
                  lineHeight={1}
                  sx={{
                    marginBottom: { xs: '0.75rem', md: '1.25rem' },
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                  }}
                >
                  Monitor domain names for changes
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Detecting infringing domain names and other threats requires a
                  high level of data quality and tools to help screen the
                  results. Identified threats should always be monitored for
                  changes. Our monitoring service will notify you of relevant
                  updates to the domain name, including rendered content, while
                  providing white-list mechanisms to reduce false alerts.
                </Typography>
                <Link
                  href="#"
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.75rem' },
                    fontWeight: 700,
                    textDecorationColor: '#1621db',
                    WebkitTextDecorationColor: '#1621db',
                    textDecorationThickness: '3px',
                    textUnderlineOffset: '8px',
                  }}
                >
                  Read the report
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={2}
          rowSpacing={4}
          direction="row-reverse"
        >
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box
                className="aspect-ratio-container-inner"
                sx={{
                  padding: { xs: '1.5rem', md: '3rem', lg: '5rem', xl: '6rem' },
                  backgroundColor: '#dee5f4',
                }}
              >
                <img
                  src={screening}
                  alt=""
                  width="563"
                  height="569"
                  css={css`
                    display: block;
                    max-width: 100%;
                    height: auto;
                    margin: 0 auto;
                    box-shadow: 10px 40px 80px rgba(0, 0, 0, 0.08);
                    border-radius: 0.3125rem;
                  `}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box className="aspect-ratio-container">
              <Box className="aspect-ratio-container-inner bg-grey">
                <Typography
                  variant="h3"
                  lineHeight={1}
                  sx={{
                    marginBottom: { xs: '0.75rem', md: '1.25rem' },
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                  }}
                >
                  E-mail security screening
                </Typography>
                <Typography variant="body2">
                  E-mail has become the most frequent attack vector for
                  IT-criminals and hackers, making all connected companies a
                  target. Guide your clients to a safer E-mail configuration by
                  bulk screening their assets using your account with Probe.net.
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box
        sx={{
          paddingY: { xs: '8rem', md: '10rem' },
          marginTop: { xs: '4rem', md: '8rem' },
          color: '#fff',
          backgroundColor: 'primary.main',
        }}
      >
        <Container>
          <Grid container spacing={0}>
            <Grid item xs={12} md={0} lg={9}>
              <Typography
                lineHeight={1.1}
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  fontWeight: 700,
                  color: '#9fe3fc',
                }}
              >
                The domain watchdog for brand protection
              </Typography>
              <Typography
                lineHeight={1.1}
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.75rem' },
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                <Link
                  href="#"
                  color="#fff"
                  sx={{
                    textDecorationColor: '#fff',
                    WebkitTextDecorationColor: '#fff',
                    textDecorationThickness: '3px',
                    textUnderlineOffset: '5px',
                  }}
                >
                  Contact us
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Layout>
  );
};

export default App;
